/**
 * useErpSourceSystem (SEE-683+)
 *
 * Direct-ERP equivalent of `useAgaveSourceSystem`. Identifies which ERP
 * an ErpConnection paidol is connected to, by reading the lazily-loaded
 * `ERPConnection` record from Redux.
 *
 * - Auto-loads the ERPConnection on first call when `selectedPaidol.erpConnectionId`
 *   is set, so callers get the data without needing to dispatch themselves.
 * - Returns the same shape as `useAgaveSourceSystem` (`isQBOClient`,
 *   `isQBDClient`, `isSage300Client`, etc.) so consumers can branch the
 *   same way regardless of which integration the paidol uses.
 * - For paidols WITHOUT an `erpConnectionId` (Agave clients, Procore,
 *   Flex, no-ERP), every flag returns false and `currentSourceSystem`
 *   is null. Those callers should continue to use `useAgaveSourceSystem`.
 *
 * No imports from `app/services/agave/*` or `app/pages/store/agaveSlice` —
 * the slug values come from our own `ErpSystem` enum, mirroring the
 * Python `ERPSystem` enum on the CDK side.
 */

import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import {
  loadErpConnection,
  resetErpConnection,
  selectErpConnection,
} from 'app/pages/store/erpConnectionSlice';
import { ErpSystem } from 'app/services/erp-connection/erpConstants';

export interface UseErpSourceSystemResult {
  /**
   * The active source-system slug for the paidol, or null when the paidol
   * has no ERPConnection. Values match the AgaveSourceSystem slug strings
   * (e.g. 'quick-books-online', 'sage-300-cre') so checks against either
   * enum work without translation.
   */
  currentSourceSystem: string | null;
  /** Connection lifecycle for spinner / disabled-state UI. */
  loading: boolean;
  /**
   * True when the current source system matches any of the provided values.
   * Mirrors the legacy `useAgaveSourceSystem.sourceSystemIn` API.
   */
  sourceSystemIn: (...sourceSystems: string[]) => boolean;
  isQBOClient: boolean;
  isQBDClient: boolean;
  isSage300Client: boolean;
  isSageIntacctClient: boolean;
  isAcumaticaClient: boolean;
}

export const useErpSourceSystem = (): UseErpSourceSystemResult => {
  const dispatch = useAppDispatch();
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const { connectionInfo, loading } = useAppSelector(selectErpConnection);

  const erpConnectionId = selectedPaidol?.erpConnectionId ?? null;

  // Lazy-load the ERPConnection when the paidol changes. When switching
  // away from an ErpConnection paidol (erpConnectionId becomes null), reset
  // the slice so stale connectionInfo from the previous paidol can't leak
  // into checks like isQBOClient on the new paidol.
  useEffect(() => {
    if (!erpConnectionId) {
      if (connectionInfo) {
        dispatch(resetErpConnection());
      }
      return;
    }
    if (connectionInfo?.connectionId === erpConnectionId) {
      return;
    }
    dispatch(loadErpConnection({ connectionId: erpConnectionId }));
  }, [dispatch, erpConnectionId, connectionInfo]);

  const currentSourceSystem = connectionInfo?.erpType ?? null;

  const sourceSystemIn = useCallback(
    (...sourceSystems: string[]): boolean =>
      currentSourceSystem ? sourceSystems.includes(currentSourceSystem) : false,
    [currentSourceSystem]
  );

  return {
    currentSourceSystem,
    loading,
    sourceSystemIn,
    isQBOClient: sourceSystemIn(ErpSystem.QUICKBOOKS_ONLINE),
    isQBDClient: sourceSystemIn(ErpSystem.QUICKBOOKS_DESKTOP),
    isSage300Client: sourceSystemIn(ErpSystem.SAGE_300_CRE),
    isSageIntacctClient: sourceSystemIn(ErpSystem.SAGE_INTACCT),
    isAcumaticaClient: sourceSystemIn(ErpSystem.ACUMATICA),
  };
};
